import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Loader2,
  Search,
  Building2,
  AlertCircle,
  AlertTriangle,
  TestTube,
  ChevronDown,
  Copy,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { envConfig } from "@/lib/config";
import { WizardLayout } from "@/components/layout/wizard-layout";
import { getAllTestScenarios } from "@/data/test-entities";
import type { WizardStepProps } from "@/types/wizard";
import type { EntityData } from "@/types/entity";
import { fetchCombinedCompanyData } from "@/services/company-data-service";
import { processCuiInput } from "@/utils/cui-utils";
import { findDuplicateCuiSession } from "@/utils/multi-session";
import { useSessionSwitcher } from "../../contexts/SessionSwitcherContext";

interface Step0Props extends WizardStepProps {
  onNext: (cui: string, entityData?: EntityData) => void;
  userAvatar?: React.ReactNode;
  entityData?: EntityData;
}

export function Step0Identification({
  onNext,
  currentStep,
  steps,
  currentSubSteps,
  onStepClick,
  userAvatar,
  entityData,
}: Step0Props) {
  const [searchParams] = useSearchParams();
  const [cui, setCui] = useState(
    () => entityData?.cui || searchParams.get("cui") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTestData, setShowTestData] = useState(false);
  const [cuiProcessing, setCuiProcessing] = useState<any>(null);
  const [validationResult, setValidationResult] = useState<any>(null);

  const { sessions, archivedSessions, currentSessionId } = useSessionSwitcher();

  const testScenarios = getAllTestScenarios();

  // Process CUI input whenever it changes
  useEffect(() => {
    if (cui.trim().length > 0) {
      const result = processCuiInput(cui);
      setCuiProcessing(result);

      if (result.error) {
        setError(result.error);
      } else {
        setError("");
      }
    } else {
      setCuiProcessing(null);
      setError("");
    }
  }, [cui]);

  // Derive duplicate-match (active or archived) from the current CUI.
  const duplicateMatch = useMemo(
    () =>
      findDuplicateCuiSession({
        cui: cuiProcessing?.cleanCui,
        sessions,
        archivedSessions,
        excludeSessionId: currentSessionId,
      }),
    [cuiProcessing?.cleanCui, sessions, archivedSessions, currentSessionId]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!cuiProcessing || !cuiProcessing.isValid) {
      setError("Vă rugăm introduceți un CUI valid");
      return;
    }

    setIsLoading(true);
    setValidationResult(null);

    try {
      // Call enhanced company data service with original input (including RO if present)
      const result = await fetchCombinedCompanyData(cui);

      if (!result.success) {
        if (result.notFound) {
          throw new Error("CUI-ul nu a fost găsit în baza de date ANAF");
        }
        throw new Error(result.error || "Eroare la preluarea datelor");
      }

      // Store validation result for display
      setValidationResult(result);

      // Add complete API responses for storage and log financial data
      const entityDataWithAPIs = {
        ...result.entityData,
        anaf_raw_data: result.anafData,
        infocui_raw_data: result.infocuiData,
        infocui_data_fetched_at: result.infocuiData
          ? new Date().toISOString()
          : null,
      };

      console.log("📊 Financial data extracted:", {
        nrSalariati: entityDataWithAPIs.nr_salariati,
        cifraAfaceri: entityDataWithAPIs.cifra_afaceri,
        active: entityDataWithAPIs.active,
        hasFinancialData: result.hasFinancialData,
        dataSource: entityDataWithAPIs.data_source,
        financialYear: entityDataWithAPIs.financial_data_year,
        hasVatRegistration: result.hasVatRegistration,
        anafVatValidated: result.anafVatValidated,
      });

      // Call the onNext callback with the enhanced entity data
      onNext(result.cleanCui || cuiProcessing.cleanCui, entityDataWithAPIs);
    } catch (err) {
      console.error("Error fetching ANAF data:", err);
      setError(err instanceof Error ? err.message : "Eroare la validarea CUI");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCUIChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCui(value);
    setValidationResult(null);
  };

  const handleTestCUISelect = (testCUI: string) => {
    setCui(testCUI);
    setError("");
  };

  return (
    <WizardLayout
      title="Identificare și Validare Date Entitate"
      description="Introduceți CUI-ul entității pentru a începe procesul de evaluare a conformității NIS 2"
      currentStep={currentStep}
      steps={steps}
      currentSubSteps={currentSubSteps}
      onStepClick={onStepClick}
      userAvatar={userAvatar}
    >
      <div className="space-y-4">
        {/* Welcome Section */}
        <div className="grid grid-cols-[max-content,1fr] gap-6 pb-4">
          <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
            <Building2 className="w-6 h-6 md:w-8 md:h-8 text-primary" />
          </div>
          <div className="grid">
            <h3 className="text-base md:text-xl font-heading font-semibold">
              Bun venit în procesul de evaluare NIS 2
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Acest asistent vă va ghida prin toate etapele necesare pentru a
              determina dacă entitatea dvs. se încadrează în domeniul de
              aplicare al Ordonanței de Urgență 155/2024 privind securitatea
              cibernetică.
            </p>
          </div>
        </div>

        {/* Information Section - Hidden for now */}
        {false && (
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <h4 className="font-heading font-semibold mb-3">
                Ce se va întâmpla în continuare:
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Vom prelua automat datele publice ale entității (denumire,
                  adresă, CAEN-uri, etc.)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Veți putea verifica și corecta toate informațiile
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  Asistentul va evalua automat aplicabilitatea legislației NIS 2
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  La final veți primi formularul de notificare precompletat
                </li>
              </ul>
            </CardContent>
          </Card>
        )}

        {/* CUI Input Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card className="border-none shadow-none">
            <CardContent className="space-y-4 md:p-0 p-0 flex flex-col">
              <div className="space-y-2">
                <Label
                  htmlFor="cui"
                  className="text-sm md:text-base font-medium"
                >
                  CUI Entitate *
                </Label>
                <div className="relative">
                  <Input
                    id="cui"
                    type="text"
                    placeholder="Ex: 12345678 sau RO12345678"
                    value={cui}
                    onChange={handleCUIChange}
                    className={`text-sm md:text-lg h-12 pr-10 ${error ? "border-destructive" : ""}`}
                    disabled={isLoading}
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-destructive text-xs md:text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {/* CUI Processing Status */}
                {cuiProcessing &&
                  cui.trim().length > 0 &&
                  !cuiProcessing.error && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs md:text-sm">
                        <CheckCircle className="w-4 h-4 text-ds-green-600" />
                        <span className="text-ds-green-600">
                          CUI:{" "}
                          <span className="font-mono">
                            {cuiProcessing.cleanCui}
                          </span>
                        </span>
                        {cuiProcessing.hasVatRegistration && (
                          <Badge variant="secondary" className="text-xs">
                            Înregistrat TVA
                          </Badge>
                        )}
                      </div>

                      {validationResult && (
                        <div className="text-xs md:text-sm">
                          {validationResult.anafVatValidated && (
                            <div className="flex items-center gap-2 text-ds-green-600">
                              <CheckCircle className="w-4 h-4" />
                              Status TVA validat cu ANAF
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                {/* Duplicate Session Warning */}
                {duplicateMatch && (
                  <div className="flex items-center gap-2 rounded-lg border border-ds-amber-500/30 bg-ds-amber-100 p-3 mt-2">
                    <AlertTriangle className="w-4 h-4 text-ds-amber-500 flex-shrink-0" />
                    <span className="text-sm text-navy-800">
                      {duplicateMatch.isArchived
                        ? "Această companie este arhivată. Dezarhiveaz-o din Companiile mele pentru a continua."
                        : `Sesiunea pentru ${duplicateMatch.session.entity_data?.cui} - ${
                            duplicateMatch.session.entity_data?.nume_entitate ||
                            "Entitate necunoscută"
                          } deja există. Te rugăm să selectezi o altă entitate.`}
                    </span>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                variant="hero"
                className="w-full h-12 text-sm md:text-lg"
                disabled={
                  !cui.trim() ||
                  isLoading ||
                  (cuiProcessing && !cuiProcessing.isValid) ||
                  duplicateMatch !== null
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preluare date în curs...
                  </>
                ) : duplicateMatch?.isArchived ? (
                  "Companie arhivată"
                ) : duplicateMatch ? (
                  "Sesiune existentă detectată"
                ) : (
                  "Preluare date automat"
                )}
              </Button>
            </CardContent>
          </Card>
        </form>

        {/* Test Data Section - Hidden for production */}
        {envConfig.IS_DEV && (
          <Card className="border-dashed border-primary/30 bg-primary/5">
            <Collapsible open={showTestData} onOpenChange={setShowTestData}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-primary/10 transition-smooth">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TestTube className="w-5 h-5 text-primary" />
                      <CardTitle className="text-base md:text-lg">
                        Date de Test - Explorează Toate Scenariile
                      </CardTitle>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-primary transition-transform ${showTestData ? "rotate-180" : ""}`}
                    />
                  </div>
                  <CardDescription className="text-xs md:text-sm">
                    {testScenarios.length} entități de test pentru demonstrarea
                    tuturor căilor prin algoritm
                  </CardDescription>
                </CardHeader>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Selectează una dintre entitățile de test de mai jos pentru
                      a explora diferite scenarii:
                    </p>

                    <div className="grid gap-3">
                      {testScenarios.map((scenario, index) => (
                        <div
                          key={scenario.cui}
                          className="flex items-center justify-between p-3 md:p-4 border rounded-lg hover:bg-muted/50 transition-smooth"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge
                                variant="outline"
                                className="text-xs md:text-sm font-mono"
                              >
                                CUI: {scenario.cui}
                              </Badge>
                              <Badge
                                variant={
                                  scenario.scenario.includes("ESENȚIALĂ")
                                    ? "destructive"
                                    : scenario.scenario.includes("IMPORTANTĂ")
                                      ? "secondary"
                                      : "outline"
                                }
                                className="text-xs md:text-sm"
                              >
                                {scenario.scenario.split(" → ")[1] || "Special"}
                              </Badge>
                            </div>
                            <h4 className="font-medium text-sm mb-1">
                              {scenario.scenario}
                            </h4>
                            <p className="text-xs text-muted-foreground">
                              {scenario.description}
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigator.clipboard.writeText(scenario.cui)
                              }
                              className="h-8 w-8 p-0"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestCUISelect(scenario.cui)}
                            >
                              Folosește
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-3 bg-ds-blue-50 border border-ds-blue-200 rounded-lg">
                      <p className="text-xs text-ds-blue-700">
                        <strong>Tip:</strong> Aceste date sunt create special
                        pentru a demonstra toate scenariile din legislația NIS
                        2. Fiecare entitate va parcurge o cale diferită prin
                        algoritm.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Information Section */}
        <Card className="bg-muted/30 mb-10">
          <CardContent className="pt-6">
            <h4 className="font-heading font-semibold mb-3 text-sm md:text-base">
              Ce se va întâmpla în continuare:
            </h4>
            <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                Vom prelua automat datele publice ale entității (denumire,
                adresă, CAEN-uri, etc.)
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                Veți putea verifica și corecta toate informațiile înainte de a
                continua
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                Sistemul va evalua automat aplicabilitatea legislației NIS 2
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                La final veți primi formularul de notificare precompletat
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </WizardLayout>
  );
}
